import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserContext } from '@incentivegames/ig-frontend-common/lib/contexts/userContext';
import { IGame, ITeamMember } from '@incentivegames/ig-types/lib/interfaces/apiContracts';

import { IInitialState } from 'interfaces/gameInterfaces';
import { IRootState } from 'store/serverInterfaces';
import * as GameHeaderContentFunctions from './GameHeaderContentFunctions';
import { dreamteamUtilities } from 'utilities/dreamteamUtilities';
import Loading from 'components/Loading';
import { GameHeaderState } from 'constants/appEnums';
import useIGTranslation from 'hooks/useIGTranslation';

import './GameHeaderContent.scss';

const GameHeaderContent = () => {
  const userContext = useContext(UserContext);
  const dispatch = useDispatch();
  const { t } = useIGTranslation();
  const currentTime = Date.now();
  const dreamteamReducer: IInitialState = useSelector((state: IRootState) => state.DreamteamReducer);
  const sportsReducer: IInitialState = useSelector((state: IRootState) => state.SportsReducer);
  const [formattedStartTime, setFormattedStartTime] = useState<string>('');
  const [formattedNextPlayerTime, setFormattedNextPlayerTime] = useState<string>('');
  const [selectedGame, setSelectedGame] = useState<IGame>();
  const [activeTeamMember, setActiveTeamMember] = useState<ITeamMember | undefined>();
  const readyToRevealPlayer = !!activeTeamMember && GameHeaderContentFunctions.isPlayerReadyToRevealed(activeTeamMember);
  const prizePerGoal = dreamteamReducer.prize?.prizeAmount;
  const hidePrize = dreamteamReducer.hidePrize;
  const currency = GameHeaderContentFunctions.getCurrency(dreamteamReducer.selectedEntry?.currencyCode ?? '');
  const winnings = dreamteamReducer.selectedEntry?.returns ?? 0;
  const goals = dreamteamReducer.selectedEntry?.totalPoints ?? 0;
  const totalBonus = dreamteamUtilities.formatCurrencyString(
    currency.symbol,
    currency.code,
    dreamteamReducer.prize?.prizeAmount ? dreamteamReducer.prize?.prizeAmount * (dreamteamReducer.prize?.prizeMultiplier ?? 1) : 0
  );
  const isEntryFinished = !!dreamteamReducer.selectedEntry && dreamteamUtilities.isEntryFinished(dreamteamReducer.selectedEntry);

  useEffect(() => {
    GameHeaderContentFunctions.handleSelectedGame(dreamteamReducer, setSelectedGame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dreamteamReducer.selectedGameId]);

  useEffect(() => {
    GameHeaderContentFunctions.handleActiveTeamMember(dreamteamReducer, setActiveTeamMember);
    GameHeaderContentFunctions.handleBalance(dreamteamReducer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dreamteamReducer.selectedEntry]);

  useEffect(() => {
    const futureGameCountdown: NodeJS.Timeout | undefined = GameHeaderContentFunctions.createFutureGameCountDown(t, {
      selectedGame,
      revealTime: selectedGame?.revealSchedule?.[0]?.revealTime,
      setFormattedStartTime
    });

    return () => {
      if (futureGameCountdown) clearInterval(futureGameCountdown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGame]);

  useEffect(() => {
    const playerRevealCountdown: NodeJS.Timeout | null = GameHeaderContentFunctions.createReadyPlayerRevealCountDown(t, {
      activeTeamMember,
      selectedGameId: dreamteamReducer.selectedGameId,
      token: userContext.user.token,
      setFormattedNextPlayerTime,
      dispatch
    });
    const playerExpireCountdown: NodeJS.Timeout | null = GameHeaderContentFunctions.createReadyPlayerExpireCountDown({
      activeTeamMember,
      selectedEntry: dreamteamReducer.selectedEntry,
      token: userContext.user.token,
      selectedGameId: dreamteamReducer.selectedGameId,
      setActiveTeamMember,
      dispatch
    });
    // console.log('playerRevealCountdown', playerRevealCountdown);
    return () => {
      if (playerRevealCountdown) clearInterval(playerRevealCountdown);
      if (playerExpireCountdown) clearInterval(playerExpireCountdown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeamMember]);

  const renderFutureGame = () => (
    <>
      <div className="game-header-content__match-title">{t('starts-in')}</div>
      <hr className="game-header-content__divide" />
      <div className="game-header-content__time">{formattedStartTime}</div>
    </>
  );

  const renderMissedGame = () => (
    <>
      <div className="game-header-content__round">{t('round')}</div>
      <div className="game-header-content__missed">{t('missed')}</div>
    </>
  );

  const renderPlayerReady = () => (
    <>
      <div className="game-header-content__match-title">{t('revealed-players-earn-you-cash')}</div>
      <hr className="game-header-content__divide" />
      <div className="game-header-content__player-ready">{t('todays-player-is-now-ready')}</div>
    </>
  );

  const renderPlayerReadSoon = () => (
    <>
      <div className="game-header-content__match-title">{t('revealed-players-earn-you-cash')}</div>
      <hr className="game-header-content__divide" />
      <div className="game-header-content__next-player">
        {t('a-new-player-is-available')} <span>{formattedNextPlayerTime}</span>
      </div>
    </>
  );

  const renderGameInPlayOrReadyToPlay = () => (
    <>
      <div className="game-header-content__game-ready">
        <div className="game-header-content__each-goal">{t('each-goal-scored-is-worth')}</div>
        <div className="game-header-content__prize">{prizePerGoal && !hidePrize ? totalBonus : ''}</div>
      </div>
      <hr className="game-header-content__divide" />
      <div className="game-header-content__good-luck">{isEntryFinished ? t('cash-paid-a-few-minutes-after-goal-confirmation') : t('good-luck')}</div>
    </>
  );

  const renderFinishedWithPoint = () => (
    <>
      <div className="game-header-content__congratulations">{t('congratulations-youve-won')}</div>
      <div className="game-header-content__winnings">{dreamteamUtilities.formatCurrencyString(currency.symbol, currency.code, winnings)}</div>
      <hr className="game-header-content__divide" />
      <div className="game-header-content__winnings-breakdown">{prizePerGoal && !hidePrize ? `${goals} ${goals === 1 ? t('goal') : t('goals')} x ${totalBonus}` : ''}</div>
    </>
  );

  const renderFinishedWithNoPoint = () => (
    <>
      <div className="game-header-content__no-score">{t('your-team-didnt-score')}</div>
      <hr className="game-header-content__divide" />
      <div className="game-header-content__better-luck">{t('better-luck-next-time')}</div>
    </>
  );

  const renderPlaceholder = () => <Loading showText={false} />;

  const headerState = GameHeaderContentFunctions.getCurrentGameHeaderState({
    game: selectedGame,
    entry: dreamteamReducer.selectedEntry,
    events: sportsReducer.events,
    readyToRevealPlayer,
    currentTime,
    formattedNextPlayerTime,
    hidePrize,
    futureGame: GameHeaderContentFunctions.isFutureWeek(selectedGame),
    prizeAmount: dreamteamReducer.prize?.prizeAmount,
    loading: dreamteamReducer.loadingDataForHeader,
    revealAnimationInProgress: dreamteamReducer?.revealAnimationInProgress
  });

  const renderContent = () => {
    switch (headerState) {
      case GameHeaderState.missedGame:
        return renderMissedGame();
      case GameHeaderState.playerReadySoon:
        return renderPlayerReadSoon();
      case GameHeaderState.playerReady:
        return renderPlayerReady();
      case GameHeaderState.gameInPlayOrReadyToPlay:
        return renderGameInPlayOrReadyToPlay();
      case GameHeaderState.finishedWithPoint:
        return renderFinishedWithPoint();
      case GameHeaderState.finishedNoPoints:
        return renderFinishedWithNoPoint();
      case GameHeaderState.futureGame:
        return renderFutureGame();
      case GameHeaderState.placeholder:
        return renderPlaceholder();
      default:
        return renderPlaceholder();
    }
  };

  return (
    <div className="game-header-content" data-test={headerState}>
      {renderContent()}
    </div>
  );
};

export default GameHeaderContent;
